import { snapToPolyline } from '../nav/geo';
import { listedMarkers, readMapLayers, type MapLayers } from './mapLayers';

// 2GIS pedestrian Routing API accepts up to three intermediate points.
export const MAX_NAVIGATION_VIA = 3;
// A museum plan is calculated in bounded API batches; its final line visits
// every stop. This is distinct from the point chooser's single-request limit.
export const MAX_PLAN_NAVIGATION_STOPS = 16;
// «Где …?» answered by points on the map (`map_show`): every fountain of the
// Lower Park fits. The backend's `tools/map_show.py` MAX_MAP_POINTS.
export const MAX_MAP_POINTS = 48;
// …and its categories (BUG-08): the backend's `map_show.MAX_KINDS`, with room for a frame joined to another.
export const MAX_MAP_CATEGORIES = 10;
// «Что успею за N минут» (`reach_within`, #216): the backend's `reach.MIN_MINUTES`…`MAX_MINUTES`.
export const REACH_MINUTES = [5, 60] as const;

export type NavigationOrigin = { lat: number; lon: number; source: string };
export const ARRIVAL_TRANSPORTS = ['car', 'taxi', 'transit', 'bus', 'train', 'tram', 'meteor', 'walk'] as const;
export type ArrivalTransport = typeof ARRIVAL_TRANSPORTS[number];
export type NavigationDestination = {
  id: string;
  title: string;
  lat: number;
  lon: number;
  ensemble: string | null;
};
export type NavigationPayload = {
  status: 'loading' | 'ready' | 'error';
  /** `points`: «где …?» — the museum's points on the map, no route (`map_show`). `where`: the «Где вы?» card —
   *  a tool needed the visitor's place and had none (INV-PH-103): no map, the buttons that tell it. */
  scope?: 'point' | 'plan' | 'arrival' | 'points' | 'places' | 'reach' | 'where';
  origin: NavigationOrigin | null;
  request_origin?: NavigationOrigin | null;
  request_location_at?: number | null;
  request_reset_at?: number | null;
  origin_selection_id?: string;
  transport?: ArrivalTransport;
  /** `scope: 'arrival'` to a museum point, not only to the park (`navigate_to` from far, 12.10.2026): by
   *  transport to the entrance, then on foot through the park — one line. */
  trip?: boolean;
  origin_label?: string;
  steps?: string[];
  location_at: number | null;
  destination?: NavigationDestination;
  via?: NavigationDestination[];
  distance_m?: number;
  duration_s?: number;
  geometry?: [number, number][];
  /** The destination's museum points on a walk that has no start yet (#208), or the points a «где …?»
   *  asked for (`scope: 'points'`): no line, markers only. */
  points?: NavigationDestination[];
  /** `scope: 'points'` of a category or several (`map_show`, BUG-08): their names, «кафе», «туалеты» — the card's title. */
  categories?: string[];
  warnings: string[];
  link?: string;
  error?: string;
  /** Overlays of the map (`mapLayers.ts`): `scope: 'places'` lists its markers layer; any scope may carry them. */
  layers?: MapLayers;
  /** `scope: 'places'` (#215): the category asked for, as the tool named it («кофе»). */
  category?: string;
  /** `scope: 'reach'` (#216): the minutes of the walking zone (5–60). */
  minutes?: number;
  /** `scope: 'where'` (INV-PH-103): the line the card sends once the visitor's GPS or address is known. */
  ask?: string;
  /** `scope: 'where'`: `'arrival'` — the card of a trip there, with no entrance buttons under the answer (v2). */
  purpose?: 'arrival';
  /** A walk with no start whose `points` are ONE chain in its order (INV-PH-101, «A → B → C»), not alternatives. */
  chain?: boolean;
};

/** The «Где вы?» card's line, bounded like a chip value: it goes out as the visitor's message. */
export const MAX_ASK_CHARS = 200;

type Obj = Record<string, unknown>;
const obj = (value: unknown): value is Obj => !!value && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const boundedText = (value: unknown, max: number): value is string => typeof value === 'string' && value.trim().length > 0 && value.length <= max;

export function validCoordinate(lat: unknown, lon: unknown): boolean {
  return finite(lat) && finite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

export function sameNavigationOrigin(a: NavigationOrigin | null | undefined, b: NavigationOrigin | null | undefined): boolean {
  return !!a && !!b && a.source === b.source && Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lon - b.lon) < 1e-7;
}

export function distanceMeters(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const radians = (value: number) => (value * Math.PI) / 180;
  const dLat = radians(b.lat - a.lat);
  const dLon = radians(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 12_742_000 * Math.asin(Math.sqrt(Math.min(1, h)));
}

function readDestination(raw: unknown): NavigationDestination | null {
  if (!obj(raw) || !boundedText(raw.id, 80) || !boundedText(raw.title, 200) ||
      !validCoordinate(raw.lat, raw.lon) || !(raw.ensemble === null || typeof raw.ensemble === 'string')) return null;
  return { id: raw.id, title: raw.title, lat: raw.lat as number, lon: raw.lon as number,
    ensemble: typeof raw.ensemble === 'string' ? raw.ensemble.slice(0, 200) : null };
}

/** Bound and validate a route before it enters transient UI state. */
export function readNavigationPayload(value: unknown): NavigationPayload | null {
  if (!obj(value) || (value.status !== 'loading' && value.status !== 'ready' && value.status !== 'error')) return null;
  const arrival = value.scope === 'arrival';
  const mapPoints = value.scope === 'points';
  const places = value.scope === 'places';
  const reach = value.scope === 'reach';
  const where = value.scope === 'where';
  const origin = obj(value.origin) ? value.origin : null;
  // A route is drawn from its start; points on the map need none.
  if (origin ? !validCoordinate(origin.lat, origin.lon) || !boundedText(origin.source, 32) : value.origin !== null || value.status === 'ready' && !mapPoints) return null;
  if (!(value.location_at === null || finite(value.location_at))) return null;
  if (value.error !== undefined && (typeof value.error !== 'string' || value.error.length > 600)) return null;
  if (value.scope !== undefined && value.scope !== 'point' && value.scope !== 'plan' && !arrival && !mapPoints && !places && !reach && !where) return null;
  if (where) return wherePayload(value, origin);
  if (value.ask !== undefined) return null;
  if (mapPoints && (value.status !== 'ready' || value.points === undefined || value.destination !== undefined || value.via !== undefined)) return null;
  if (value.categories !== undefined && (!mapPoints || !Array.isArray(value.categories) || value.categories.length === 0 ||
      value.categories.length > MAX_MAP_CATEGORIES || value.categories.some((name) => !boundedText(name, 40)))) return null;
  const requestOrigin = obj(value.request_origin) ? value.request_origin : null;
  if ((arrival || value.request_origin !== undefined) && (value.request_origin !== null && (!requestOrigin || !validCoordinate(requestOrigin.lat, requestOrigin.lon) || !boundedText(requestOrigin.source, 32)))) return null;
  if (value.request_location_at !== undefined && value.request_location_at !== null && !finite(value.request_location_at)) return null;
  if (value.request_reset_at !== undefined && value.request_reset_at !== null && !finite(value.request_reset_at)) return null;
  if (value.origin_selection_id !== undefined && !boundedText(value.origin_selection_id, 120)) return null;
  if (arrival && !ARRIVAL_TRANSPORTS.includes(value.transport as ArrivalTransport)) return null;
  if (value.trip !== undefined && (value.trip !== true || !arrival)) return null;
  if (value.origin_label !== undefined && !boundedText(value.origin_label, 300)) return null;
  if (value.steps !== undefined && (!Array.isArray(value.steps) || value.steps.length > 32 || value.steps.some((step) => !boundedText(step, 400)))) return null;

  const result: NavigationPayload = {
    status: value.status,
    origin: origin ? { lat: origin.lat as number, lon: origin.lon as number, source: origin.source as string } : null,
    location_at: value.location_at,
    warnings: Array.isArray(value.warnings)
      ? value.warnings.filter((warning): warning is string => typeof warning === 'string').slice(0, 8).map((warning) => warning.slice(0, 240))
      : [],
  };
  if (value.scope !== undefined) result.scope = value.scope as NavigationPayload['scope'];
  if (Array.isArray(value.categories)) result.categories = [...new Set(value.categories as string[])];
  if (value.request_location_at !== undefined) result.request_location_at = value.request_location_at as number | null;
  if (value.request_reset_at !== undefined) result.request_reset_at = value.request_reset_at as number | null;
  if (typeof value.origin_selection_id === 'string') result.origin_selection_id = value.origin_selection_id;
  if (arrival || value.request_origin !== undefined) {
    result.request_origin = requestOrigin ? { lat: requestOrigin.lat as number, lon: requestOrigin.lon as number, source: requestOrigin.source as string } : null;
  }
  if (arrival) result.transport = value.transport as ArrivalTransport;
  if (value.trip === true) result.trip = true;
  if (typeof value.origin_label === 'string') result.origin_label = value.origin_label;
  if (value.status === 'ready' && Array.isArray(value.steps)) result.steps = value.steps as string[];

  if (value.destination !== undefined) {
    const destination = obj(value.destination) ? value.destination : null;
    if (!destination || !boundedText(destination.id, 80) || !boundedText(destination.title, 200) ||
        !validCoordinate(destination.lat, destination.lon) ||
        !(destination.ensemble === null || typeof destination.ensemble === 'string')) return null;
    result.destination = {
      id: destination.id,
      title: destination.title,
      lat: destination.lat as number,
      lon: destination.lon as number,
      ensemble: typeof destination.ensemble === 'string' ? destination.ensemble.slice(0, 200) : null,
    };
  }
  if (value.points !== undefined) {
    // On the «no start» failure of a museum walk (a route with a start draws its line instead), and
    // on the map of a «где …?» — with or without the visitor's position.
    const noStart = value.status === 'error' && value.origin === null && !arrival && value.scope !== 'plan' && !mapPoints;
    if (!noStart && !mapPoints || !Array.isArray(value.points) || value.points.length === 0 ||
        value.points.length > (mapPoints ? MAX_MAP_POINTS : MAX_PLAN_NAVIGATION_STOPS)) return null;
    const points: NavigationDestination[] = [];
    for (const raw of value.points) {
      const point = readDestination(raw);
      if (!point) return null;
      points.push(point);
    }
    if (new Set(points.map((point) => point.id)).size !== points.length) return null;
    result.points = points;
  }
  if (value.chain !== undefined) {
    // INV-PH-101: only a walk with no start carries a chain, and a chain is two points at least
    if (value.chain !== true || !result.points || result.points.length < 2 || mapPoints) return null;
    result.chain = true;
  }
  if (value.via !== undefined) {
    // A trip (`trip`) to a whole plan rides to the entrance, then walks every stop (12.10.2026).
    const maxVia = arrival ? (value.trip === true ? MAX_PLAN_NAVIGATION_STOPS - 1 : 0) : value.scope === 'plan' ? MAX_PLAN_NAVIGATION_STOPS - 1 : MAX_NAVIGATION_VIA;
    if (!Array.isArray(value.via) || value.via.length > maxVia) return null;
    const stops: NavigationDestination[] = [];
    for (const raw of value.via) {
      if (!obj(raw) || !boundedText(raw.id, 80) || !boundedText(raw.title, 200) ||
          !validCoordinate(raw.lat, raw.lon) || !(raw.ensemble === null || typeof raw.ensemble === 'string')) return null;
      stops.push({ id: raw.id, title: raw.title, lat: raw.lat as number, lon: raw.lon as number,
        ensemble: typeof raw.ensemble === 'string' ? raw.ensemble.slice(0, 200) : null });
    }
    if (new Set(stops.map((stop) => stop.id)).size !== stops.length || stops.some((stop) => stop.id === result.destination?.id)) return null;
    result.via = stops;
  }

  if (places) return placesPayload(value, result);
  if (reach) return reachPayload(value, result);
  if (value.status === 'ready' && !mapPoints) {
    if (!result.origin || !result.destination || !finite(value.distance_m) || value.distance_m <= 0 || value.distance_m > (arrival ? 300_000 : 15_000) ||
        !finite(value.duration_s) || value.duration_s <= 0 || value.duration_s > (arrival ? 86_400 : 18_000) ||
        !Array.isArray(value.geometry) || value.geometry.length < 2 || value.geometry.length > (arrival ? 12_000 : 2_000)) return null;
    const geometry: [number, number][] = [];
    for (const pair of value.geometry) {
      if (!Array.isArray(pair) || pair.length !== 2 || !validCoordinate(pair[1], pair[0])) return null;
      geometry.push([pair[0] as number, pair[1] as number]);
    }
    // A walking route around the world is never a useful map frame. This also
    // keeps malformed responses from zooming MapGL to an unusable scale.
    const lons = geometry.map(([lon]) => lon);
    const lats = geometry.map(([, lat]) => lat);
    const maxSpan = arrival ? 8 : 1;
    if (Math.max(...lons) - Math.min(...lons) > maxSpan || Math.max(...lats) - Math.min(...lats) > maxSpan) return null;
    const first = { lat: geometry[0]![1], lon: geometry[0]![0] };
    const last = { lat: geometry.at(-1)![1], lon: geometry.at(-1)![0] };
    if (distanceMeters(first, result.origin) > 150 || distanceMeters(last, result.destination) > 120) return null;
    // Every selected intermediate stop must actually sit on the received line,
    // in the requested order. A pin near a different route is misleading.
    let remaining = geometry;
    for (const stop of result.via ?? []) {
      // A plan can pass a fountain early and visit it again after another stop.
      // Match only the remaining line, preserving the previous segment's ratio
      // instead of projecting onto an earlier pass and rejecting a valid loop.
      const snap = snapToPolyline(remaining, [stop.lon, stop.lat]);
      if (!snap || snap.offRouteM > 120) return null;
      remaining = [[snap.at[0], snap.at[1]], ...remaining.slice(snap.index + 1)];
    }
    result.distance_m = value.distance_m;
    result.duration_s = value.duration_s;
    result.geometry = geometry;
  }

  if (value.layers !== undefined) {
    const layers = readMapLayers(value.layers);
    if (!layers) return null;
    result.layers = layers;
  }
  return withLinkAndError(value, result);
}

function withLinkAndError(value: Obj, result: NavigationPayload): NavigationPayload {
  if (typeof value.link === 'string') {
    const link = safe2gisLink(value.link);
    if (link) result.link = link;
  }
  if (typeof value.error === 'string') result.error = value.error.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 600);
  return result;
}

/** «Где вы?» (INV-PH-103, owner 15.10.2026): a tool needed the visitor's place and had none. Never a map: no
 *  start, no points, no line; the card's buttons send `ask` once the place is known. */
function wherePayload(value: Obj, origin: Obj | null): NavigationPayload | null {
  if (value.status !== 'error' || origin || value.destination !== undefined || value.via !== undefined ||
      value.points !== undefined || value.geometry !== undefined || value.layers !== undefined || value.link !== undefined ||
      value.chain !== undefined || !boundedText(value.ask, MAX_ASK_CHARS)) return null;
  if (!(value.location_at === null || finite(value.location_at))) return null;
  if (value.purpose !== undefined && value.purpose !== 'arrival') return null;
  const result: NavigationPayload = { status: 'error', scope: 'where', origin: null, location_at: value.location_at, warnings: [],
    ask: (value.ask as string).replace(/[\u0000-\u001f\u007f]/g, ' ').trim() };
  if (value.purpose === 'arrival') result.purpose = 'arrival';
  return withLinkAndError(value, result);
}

/** «Places near the visitor» (`places_nearby`, #215): a ready card lists ONE markers layer (≤ 20 places)
 *  around its start; a failed one carries the reason and, with a start, a 2GIS search link. No route. */
function placesPayload(value: Obj, result: NavigationPayload): NavigationPayload | null {
  if (value.destination !== undefined || value.via !== undefined || value.points !== undefined || value.geometry !== undefined) return null;
  if (value.category !== undefined && !boundedText(value.category, 40)) return null;
  if (typeof value.category === 'string') result.category = value.category;
  if (value.status === 'ready') {
    const layers = readMapLayers(value.layers);
    if (!result.origin || !layers || !listedMarkers(layers)) return null;
    result.layers = layers;
  } else if (value.layers !== undefined) {
    return null;
  }
  return withLinkAndError(value, result);
}

/** «Что успею за N минут» (`reach_within`, #216): a ready card draws the 2GIS walking zone (one polygon layer
 *  or more) and, when the zone holds any, ONE markers layer of the museum's points; a failed one carries the
 *  reason only. No route, no link. */
function reachPayload(value: Obj, result: NavigationPayload): NavigationPayload | null {
  if (value.destination !== undefined || value.via !== undefined || value.points !== undefined || value.geometry !== undefined) return null;
  const [low, high] = REACH_MINUTES;
  if (!Number.isInteger(value.minutes) || (value.minutes as number) < low || (value.minutes as number) > high) return null;
  result.minutes = value.minutes as number;
  if (value.status === 'ready') {
    const layers = readMapLayers(value.layers);
    if (!result.origin || !layers || !layers.layers.some((layer) => layer.kind === 'polygon') ||
        layers.layers.filter((layer) => layer.kind === 'markers').length > 1) return null;
    result.layers = layers;
  } else if (value.layers !== undefined) {
    return null;
  }
  return withLinkAndError({ ...value, link: undefined }, result);
}

/** A route through a plan's stops — walked, or a trip that rides to the entrance first (`trip` with stops). */
export function isPlanRoute(route: Pick<NavigationPayload, 'scope' | 'via' | 'trip'>): boolean {
  return route.scope === 'plan' || (route.trip === true && (route.via?.length ?? 0) > 0);
}

export function safe2gisLink(raw: string): string | null {
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    const approvedHost = host === '2gis.ru' || host.endsWith('.2gis.ru') || host === '2gis.com' || host.endsWith('.2gis.com');
    const hasCredential = [...url.searchParams.keys()].some((name) => /^(?:key|api[_-]?key|token|access_token)$/i.test(name));
    return url.protocol === 'https:' && approvedHost && !url.username && !url.password && !hasCredential ? url.toString() : null;
  } catch {
    return null;
  }
}

/** «Кафе и туалеты» — a «где …?» card's title for its categories (the backend's `map_show.categories_title`). */
export function categoriesTitle(categories: readonly string[]): string {
  const said = categories.length > 1 ? `${categories.slice(0, -1).join(', ')} и ${categories.at(-1)}` : categories[0] ?? '';
  return said ? said[0]!.toUpperCase() + said.slice(1) : '';
}
